import { Component, Input } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';
import {
  BoostGroupFormGroup,
  UrgencyBoostExtension,
  UrgencyBoostFormGroup,
  createBoostGroupFormGroup,
  makeBadgeId,
} from '../../model';
import { getExtensionRules } from '../../../../../shared/all-extension-rules';

@Component({
  selector: 'urgency-boost-form-appearance',
  templateUrl: './urgency-boost-form-appearance.component.html',
  styleUrls: ['./urgency-boost-form-appearance.component.scss'],
  standalone: false,
})
export class UrgencyBoostFormAppearanceComponent {
  @Input() formGroup: FormGroup<UrgencyBoostFormGroup>;

  constructor(private rulesDynamicFormService: RulesDynamicFormService) {}

  get boostGroupsArray(): FormArray<FormGroup<BoostGroupFormGroup>> {
    return this.formGroup.controls.boostGroups;
  }

  addBoostGroup(): void {
    const nextId = this.boostGroupsArray.length;
    this.boostGroupsArray.push(this.buildBoostGroup(nextId));
  }

  removeBoostGroup(index: number): void {
    if (this.boostGroupsArray.length <= 1) return;
    this.boostGroupsArray.removeAt(index);
  }

  duplicateBoostGroup(index: number): void {
    const source = this.boostGroupsArray.at(index);
    const sourceValue = source.getRawValue() as UrgencyBoostExtension.BoostGroup;
    const nextId = this.nextGroupId();
    // Each badge in the cloned group needs a fresh string ID so two badges
    // never share an id across the form — track-by and per-badge dedup rely
    // on uniqueness across the entire form.
    const badges = (sourceValue.badges ?? []).map((b) => ({ ...b, id: makeBadgeId() }));
    const cloned = createBoostGroupFormGroup(
      nextId,
      { ...sourceValue, id: nextId, badges },
      (rules) =>
        this.rulesDynamicFormService.initRulesFormArray(rules, getExtensionRules()) as FormArray<
          FormGroup<TypedRuleGroup>
        >,
    );
    this.boostGroupsArray.insert(index + 1, cloned);
    this.boostGroupsArray.markAsDirty();
  }

  private nextGroupId(): number {
    const existingIds = this.boostGroupsArray.controls.map((g) => g.controls.id.value);
    return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;
  }

  trackByGroupId(_: number, group: FormGroup<BoostGroupFormGroup>): number {
    return group.controls.id.value;
  }

  private buildBoostGroup(id: number): FormGroup<BoostGroupFormGroup> {
    return createBoostGroupFormGroup(
      id,
      { id },
      (rules) =>
        this.rulesDynamicFormService.initRulesFormArray(rules, getExtensionRules()) as FormArray<
          FormGroup<TypedRuleGroup>
        >,
    );
  }
}
